// frontend/packages/business-console/src/services/cmsAiService.js
//
// Client for UpRyzze AI (the conversational CMS). See
// docs/design/upryzze-ai-cms.md. The tenant is attached automatically by the
// api client's x-business-id interceptor, so we never pass business/slug.

import { api } from '@upryzze/services';

// A conversation turn is a model call, and apply/promote are GitHub writes.
// Both can outrun the api client's 30s default. That matters more than the
// wait: an axios timeout carries no HTTP status, so the shared response
// interceptor classifies it as a transient failure and REPLAYS the request.
// On /message that double-bills the tenant; on /apply and /promote it can
// write the same commit or merge twice. Give these three the headroom to
// finish on the first attempt.
const AI_TURN_TIMEOUT_MS = 120000;

// Cheap discovery: is UpRyzze AI enabled for this business + this admin? Used
// to gate the page. Returns { enabled: false } for anyone not allowlisted.
export async function getCmsAiStatus() {
  try {
    const { data } = await api.get('/v2/cms-ai/status');
    return data || { enabled: false };
  } catch {
    return { enabled: false };
  }
}

// Advance the conversation by one turn. `state` is the opaque FSM state the
// server returned last turn (omit on the first message). Returns
// { reply, state, proposal?, pages? }.
export async function sendCmsAiMessage({ state, message }) {
  const { data } = await api.post(
    '/v2/cms-ai/message',
    { state, message },
    { timeout: AI_TURN_TIMEOUT_MS }
  );
  return data;
}

// Commit an approved copy edit to the tenant's uat (draft) branch. `page` is the
// locked page from the conversation state; `changes` are the proposal's
// per-field changes the client approved. Returns
// { ok, applied?, previewUrl?, commitSha?, path?, message }. uat-only: promoting
// the draft to the live site is a separate step.
export async function applyCmsAiEdit({ page, changes }) {
  const { data } = await api.post(
    '/v2/cms-ai/apply',
    { page, changes },
    { timeout: AI_TURN_TIMEOUT_MS }
  );
  return data;
}

// Poll after an apply: { hasPendingChange, previewReady, previewUrl }. Used to
// tell the client when their preview is live and to enable Publish. Degrades to
// a not-ready shape on any error so the caller can just keep polling.
export async function getCmsAiPreviewStatus({ slug } = {}) {
  try {
    const { data } = await api.get('/v2/cms-ai/preview-status', {
      params: slug ? { slug } : {},
    });
    return (
      data || { hasPendingChange: false, previewReady: false, previewUrl: null }
    );
  } catch {
    return { hasPendingChange: false, previewReady: false, previewUrl: null };
  }
}

// Publish the reviewed draft to the live site (merge uat -> prod). `page` is only
// used to build the live deep link. Returns { ok, prodUrl?, mergeCommitSha?,
// message }.
export async function promoteCmsAi({ page } = {}) {
  const { data } = await api.post(
    '/v2/cms-ai/promote',
    { page },
    { timeout: AI_TURN_TIMEOUT_MS }
  );
  return data;
}
